// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { changeSelectedImage } from 'store/reducers/projectImageSlice';
import styled from 'styled-components';
import { AppRoutes } from 'utilities/router/AppRoutes';
import ImagePreview from './ImagePreview';
import { updateIndex } from 'store/reducers/projectEvaluationSlice';
import { ImagesContext } from '../images/ImagesContext';
import { Button } from '@tls/treact-ui';
import {
  IconChevronSingleDown,
  IconChevronSingleDownEnd,
  IconChevronSingleUp,
  IconChevronSingleUpEnd,
  IconEdit,
} from '@tls/treact-ui/icons';

export type Props = {
  version: string;
};

export default function ResultPreview({ version }: Props) {
  const { t } = useTranslation();

  const activeMd5 = useAppSelector(a => a.evaluation.activeMd5);
  const index = useAppSelector(a => a.evaluation.index);
  const length = useAppSelector(a => a.evaluation.length);
  const navigate = useNavigate();
  const { id } = useParams();
  const { images } = useContext(ImagesContext);
  const imageName = useMemo(() => images?.find(l => l?.imageMd5 === activeMd5)?.imageName ?? '', [images, activeMd5]);

  const dispatch = useAppDispatch();
  const onEdit = useCallback(() => {
    dispatch(changeSelectedImage(activeMd5));
    navigate(AppRoutes.ProjectLabelFullPath(id ?? ''));
  }, [activeMd5, dispatch, id, navigate]);

  const onChange = useCallback(
    (newIndex: number) => () => {
      dispatch(updateIndex(newIndex));
    },
    [dispatch]
  );

  return (
    <Container>
      <ButtonContainer>
        <ImageName data-testid={`evaluation-preview-${version}-image-name`}>{imageName}</ImageName>
        <Button
          icon={<IconChevronSingleUpEnd />}
          variant='secondary'
          disabled={index <= 0}
          onClick={onChange(0)}
          data-testid={`evaluation-preview-${version}-first-button`}
        />
        <Button
          icon={<IconChevronSingleUp />}
          variant='secondary'
          disabled={index <= 0}
          onClick={onChange(index - 1)}
          data-testid={`evaluation-preview-${version}-previous-button`}
        />
        <Button
          icon={<IconChevronSingleDown />}
          variant='secondary'
          disabled={index === length - 1 || index === -1}
          onClick={onChange(index + 1)}
          data-testid={`evaluation-preview-${version}-next-button`}
        />
        <Button
          icon={<IconChevronSingleDownEnd />}
          variant='secondary'
          disabled={index === length - 1 || index === -1}
          onClick={onChange(length - 1)}
          data-testid={`evaluation-preview-${version}-last-button`}
        />
        <Button
          icon={<IconEdit />}
          label={t('project.label.edit')}
          variant='secondary'
          disabled={index === -1}
          onClick={onEdit}
          data-testid={`evaluation-preview-${version}-edit-label-button`}
        />
      </ButtonContainer>
      {id && activeMd5 && <ImagePreview projectId={id} imageId={activeMd5} version={version} />}
    </Container>
  );
}

const ButtonContainer = styled.div`
  display: flex;
  column-gap: 0.5rem;
  padding: 0.5rem;
  align-items: center;
`;

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
`;
const ImageName = styled.div`
  flex-grow: 1;
  width: 0px;
  white-space: normal;
  line-height: 1.5em;
  max-height: 3em;
  overflow: hidden;
  text-overflow: ellipsis;
`;
